import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dropdown, Popover } from 'antd';
import jsCookie from 'js-cookie';
import { LangText } from '../LangText';
import { preFixImgUrl } from '@/utils';
import { useRootStore } from '@/contexts';
import { APP_LOCALES, LOCALE_LABELS, isAppLocale, type AppLocale } from '@/constants/appLocale';
import { PRIMARY_NAV_ITEMS } from '@/constants/primaryNavigation';
import aiIcon from '@/assets/svg/ai-icon.svg';
import languageIcon from '@/assets/svg/language.svg';
import mineIcon from '@/assets/svg/mine.svg';
import { openAuthModal } from '@/components/AuthModal/authModalController';

const HEADER_DROPDOWN_ITEM_CLASS =
  '!min-h-[44px] !px-4 !py-3 !text-[16px] !leading-[22px] !text-[#000]';

const HEADER_DROPDOWN_MENU_CLASS = '[&_.ant-dropdown-menu-item]:!min-h-[44px] [&_.ant-dropdown-menu-item]:!px-4 [&_.ant-dropdown-menu-item]:!py-3 [&_.ant-dropdown-menu-item]:!text-[16px] [&_.ant-dropdown-menu-item]:!leading-[22px] [&_.ant-dropdown-menu-item]:!text-[#000]';

function ChevronDown({ className }: { className?: string }) {
  return (
    <svg className={className ?? 'w-4 h-4'} viewBox="0 0 20 20" fill="none">
      <path d="M5.25 8.25L10 13l4.75-4.75" stroke="#000" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function isNavActive(path: string, pathname: string) {
  if (path === '/airport-luggage-delivery') {
    return pathname.startsWith('/airport-luggage');
  }
  if (path === '/coupon' || path === '/coupon-detail' || path === '/coupon-list') {
    return pathname.startsWith('/coupon');
  }
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function Header() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const { userStore } = useRootStore();
  const navigate = useNavigate();
  const { language } = i18n;

  const languageLabel = isAppLocale(language)
    ? LOCALE_LABELS[language]
    : LOCALE_LABELS.zh_CN;

  const handleLangChange = ({ key }: { key: string }) => {
    if (key === language) return;
    if (!isAppLocale(key)) return;
    jsCookie.set('i18next', key);
    localStorage.setItem('i18next', key);
    window.location.reload();
  };

  const langsOptions = APP_LOCALES.map((key: AppLocale) => ({
    key,
    label: LOCALE_LABELS[key],
    className: HEADER_DROPDOWN_ITEM_CLASS,
    onClick: handleLangChange,
  }));

  const userMenuOptions = [
    {
      key: 'profile',
      label: <LangText lngKey="layout.header.user.profile" />,
      className: HEADER_DROPDOWN_ITEM_CLASS,
      onClick: () => {
        navigate('/profile');
      },
    },
    {
      key: 'logout',
      label: <LangText lngKey="layout.header.user.logout" />,
      className: HEADER_DROPDOWN_ITEM_CLASS,
      onClick: () => {
        userStore.logOut();
      },
    },
  ];

  return (
    <header className="sticky top-0 z-40 h-20 bg-white">
      <div className="mx-auto flex h-full max-w-[1560px] items-center px-[60px]">
        <div className="shrink-0">
          <Link to="/">
            <img
              src={preFixImgUrl('41683676bd21e8531742d57a8f7a2570.png')}
              className="h-[40px] w-auto"
              draggable={false}
            />
          </Link>
        </div>

        <nav className="flex flex-1 justify-center items-center gap-[50px] min-w-[500px]">
          {PRIMARY_NAV_ITEMS.map(({ lngKey, path }) => {
            const isActive = isNavActive(path, location.pathname);

            return (
              <Link
                key={path}
                to={path}
                className={`relative shrink-0 text-[18px] leading-[25px] font-bold text-[#000] after:absolute after:top-full after:mt-[4px] after:left-0 after:h-[2px] after:w-full after:origin-center after:scale-x-0 after:bg-primary after:content-[''] after:transition-transform after:duration-300 after:ease-out hover:after:scale-x-100 ${
                  isActive ? 'after:scale-x-100' : ''
                }`}
              >
                <LangText lngKey={lngKey} />
              </Link>
            );
          })}
        </nav>

        <div className="flex shrink-0 items-center gap-8 text-[16px] text-[#333333]">
          <Link
            to="/ai-trip"
            className="flex h-[44px] items-center gap-[8px] rounded-full bg-secondary pl-[6px] pr-[15px] transition-opacity hover:opacity-90"
          >
            <span className="flex h-[33px] w-[33px] shrink-0 items-center justify-center rounded-full bg-primary">
              <img
                src={aiIcon}
                alt=""
                className="h-[22px] w-[22px] object-contain"
              />
            </span>
            <LangText lngKey="layout.header.aiTrip" className="text-[#000] font-[16px]" />
          </Link>

          <Dropdown
            menu={{ items: langsOptions, className: HEADER_DROPDOWN_MENU_CLASS }}
            placement="bottomRight"
          >
            <button type="button" className="flex gap-2 items-center focus:outline-none">
              <img src={languageIcon} alt="" className="h-[24px] w-[24px] shrink-0 text-[#000] object-contain" />
              <div className="flex gap-1 items-center">
                <span className="text-[16px] text-[#000]">{languageLabel}</span>
                <ChevronDown />
              </div>
            </button>
          </Dropdown>

          <Popover
            placement="bottomRight"
            trigger={['hover']}
            content={
              <img
                src={preFixImgUrl('20c61a1a949b9452ffaf0c38bd6107db.png')}
                alt=""
                className="h-[160px] w-[160px] object-contain"
                draggable={false}
              />
            }
          >
            <button type="button" className="flex gap-1 items-center focus:outline-none">
              <LangText lngKey="layout.header.mobile" className="text-[16px] text-[#000]" />
              <ChevronDown />
            </button>
          </Popover>

          {userStore.user ? (
            <Dropdown
              menu={{ items: userMenuOptions, className: HEADER_DROPDOWN_MENU_CLASS }}
              placement="bottomRight"
            >
              <button type="button" className="flex gap-3 items-center focus:outline-none">
                <img src={mineIcon} alt="" className="h-[24px] w-[24px] shrink-0 text-[#000] object-contain" />
                <div className="flex gap-1 items-center">
                  <LangText lngKey="layout.header.user.mine" className="text-[16px] text-[#000]" />
                  <ChevronDown />
                </div>
              </button>
            </Dropdown>
          ) : (
            <button
              type="button"
              onClick={() => openAuthModal({ reason: 'manual' })}
              className="h-[44px] rounded-full bg-primary px-[22px] leading-[44px]"
            >
              <LangText lngKey="layout.header.auth.loginOrRegister" className="text-[16px] text-white" />
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
